/**
 * Admin per-page regeneration modal.
 *
 * Opens from `OrderShow` for orders in `PREVIEW_READY`. Lets the admin pick a
 * page, optionally provide a free-form `additionalPrompt`, and (under the
 * Advanced collapse) override `imagePrompt`, `text`, `shotType`, and `setting`
 * before re-running the targeted page generation.
 *
 * The admin endpoint (`POST /api/admin/orders/:id/regenerate-page`) does NOT
 * decrement the user-facing page regeneration counter.
 *
 * Page image URLs are fetched on mount via the existing
 * `GET /api/admin/orders/:id/preview` endpoint — the admin order detail does
 * not include them. The original page JSON (imagePrompt/text/shotType/setting)
 * lives in the LangGraph checkpoint and is NOT exposed via the admin REST API,
 * so the Advanced fields start blank — admin types only the values they want
 * to override (empty fields are omitted from the request, leaving the existing
 * values untouched on the server).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Collapse,
  Image,
  Button,
  Space,
  Typography,
  App,
} from 'antd';
import { SHOT_TYPES, type RegeneratePageRequest } from '@woolaby/types';

import { axiosInstance } from '@/providers/axiosInstance';

const { Text } = Typography;

interface RegeneratePageModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  onSuccess: () => void;
}

interface AdvancedFormValues {
  imagePrompt?: string;
  text?: string;
  shotType?: (typeof SHOT_TYPES)[number];
  setting?: string;
}

interface PreviewResponse {
  pageUrls: string[];
}

export function RegeneratePageModal({
  open,
  onClose,
  orderId,
  onSuccess,
}: RegeneratePageModalProps): ReactElement {
  const { message } = App.useApp();
  const [form] = Form.useForm<AdvancedFormValues>();

  const [pageImageUrls, setPageImageUrls] = useState<string[]>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch page URLs when the modal opens.
  useEffect(() => {
    if (!open) return;
    setLoadingPages(true);
    axiosInstance
      .get<PreviewResponse>(`/api/admin/orders/${orderId}/preview`)
      .then((res) => {
        setPageImageUrls(res.data.pageUrls ?? []);
      })
      .catch((error: { message?: string }) => {
        message.error(
          `Failed to load page previews: ${error?.message ?? 'Unknown error'}`
        );
      })
      .finally(() => {
        setLoadingPages(false);
      });
  }, [open, orderId, message]);

  // Reset state on close.
  useEffect(() => {
    if (!open) {
      setSelectedPageIndex(0);
      setAdditionalPrompt('');
      setPageImageUrls([]);
      form.resetFields();
    }
  }, [open, form]);

  // Reset Advanced overrides when the selected page changes — the admin types
  // only the fields they want to override, so keep the form blank by default.
  useEffect(() => {
    form.resetFields();
  }, [selectedPageIndex, form]);

  const pageOptions = useMemo(
    () =>
      pageImageUrls.map((_, i) => ({
        value: i,
        label: `Page ${String(i + 1)}`,
      })),
    [pageImageUrls]
  );

  const shotTypeOptions = useMemo(
    () => SHOT_TYPES.map((s) => ({ value: s, label: s })),
    []
  );

  const safeIndex = Math.min(
    Math.max(selectedPageIndex, 0),
    Math.max(pageImageUrls.length - 1, 0)
  );
  const currentImage = pageImageUrls[safeIndex];

  const handleSubmit = useCallback(async (): Promise<void> => {
    const advanced = form.getFieldsValue();

    // Strip empty / whitespace-only string overrides.
    const overrides: NonNullable<RegeneratePageRequest['pageOverrides']> = {};
    if (advanced.imagePrompt?.trim()) {
      overrides.imagePrompt = advanced.imagePrompt.trim();
    }
    if (advanced.text?.trim()) {
      overrides.text = advanced.text.trim();
    }
    if (advanced.shotType) {
      overrides.shotType = advanced.shotType;
    }
    if (advanced.setting?.trim()) {
      overrides.setting = advanced.setting.trim();
    }

    const hasOverrides = Object.keys(overrides).length > 0;

    const body: RegeneratePageRequest = {
      pageIndex: safeIndex,
      ...(additionalPrompt.trim() && {
        additionalPrompt: additionalPrompt.trim(),
      }),
      ...(hasOverrides && { pageOverrides: overrides }),
    };

    setSubmitting(true);
    try {
      await axiosInstance.post(
        `/api/admin/orders/${orderId}/regenerate-page`,
        body
      );
      message.success('Page regeneration started');
      onSuccess();
      onClose();
    } catch (error) {
      const err = error as { message?: string };
      message.error(
        `Failed to regenerate page: ${err?.message ?? 'Unknown error'}`
      );
    } finally {
      setSubmitting(false);
    }
  }, [form, safeIndex, additionalPrompt, orderId, message, onSuccess, onClose]);

  const hasPages = pageImageUrls.length > 0;

  return (
    <Modal
      title="Regenerate Page"
      open={open}
      onCancel={onClose}
      width={640}
      footer={
        <Space>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={!hasPages || loadingPages}
            onClick={() => {
              void handleSubmit();
            }}
          >
            Regenerate
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>Page</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            value={hasPages ? safeIndex : undefined}
            options={pageOptions}
            onChange={(value: number) => setSelectedPageIndex(value)}
            disabled={loadingPages || !hasPages}
            placeholder={loadingPages ? 'Loading pages…' : 'No pages available'}
          />
        </div>

        {currentImage && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={currentImage}
              alt={`Page ${String(safeIndex + 1)} preview`}
              style={{ maxWidth: 240, maxHeight: 240 }}
            />
          </div>
        )}

        <div>
          <Text strong>Additional prompt</Text>
          <Input.TextArea
            rows={3}
            value={additionalPrompt}
            onChange={(e) => setAdditionalPrompt(e.target.value)}
            placeholder="Optional steering instructions for the regeneration."
            disabled={submitting}
            style={{ marginTop: 8 }}
          />
        </div>

        <Collapse
          items={[
            {
              key: 'advanced',
              label: 'Advanced (override page JSON)',
              children: (
                <>
                  <Text
                    type="secondary"
                    style={{ display: 'block', marginBottom: 12 }}
                  >
                    Leave fields empty to keep the existing page values. Only
                    non-empty fields are sent as overrides.
                  </Text>
                  <Form form={form} layout="vertical" disabled={submitting}>
                    <Form.Item label="Image prompt" name="imagePrompt">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item label="Text" name="text">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item label="Shot type" name="shotType">
                      <Select
                        options={shotTypeOptions}
                        allowClear
                        placeholder="No override"
                      />
                    </Form.Item>
                    <Form.Item label="Setting" name="setting">
                      <Input />
                    </Form.Item>
                  </Form>
                </>
              ),
            },
          ]}
        />
      </Space>
    </Modal>
  );
}
